/**
 * Buddies: people who moved to Austria from their home country and help
 * students from that country as volunteers on the Migrada team (community
 * answers, guides, the students who share their case with them). Plans are
 * written by Noah. Every buddy accepts the Buddy Agreement in the app first.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REAL PEOPLE ONLY.
 * An entry here puts a person's name, university and face on migrada.eu. Add
 * someone only when the Team view shows "Photo: yes" for them (their consent,
 * recorded in the app) and only with details that are true today. When it
 * shows "Photo withdrawn", remove the entry within 7 days. No placeholders, no "coming soon" profiles, no invented people:
 * a made-up buddy is fabricated proof and trust is the whole product.
 *
 * While this list is empty the site says so plainly and asks students already
 * in Austria to apply. The moment a real buddy is added, the homepage shows
 * them and the plan form marks their country.
 *
 * Photos: self-host under public/buddies/<id>.jpg. The production CSP blocks
 * external image hosts.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface Buddy {
  /** Stable key, also the photo filename. */
  id: string;
  /** ISO 3166-1 alpha-2 of the country whose students this buddy helps. */
  countryCode: string;
  /** Display name of that country, e.g. "Kazakhstan". */
  countryName: string;
  /** Name exactly as the buddy agreed to show it, e.g. "Aruzhan B." */
  name: string;
  university: string;
  /** Only when known and true today; the card leaves it out otherwise. */
  programme?: string;
  /** Austrian city they live in now. */
  city: string;
  /** Year they moved to Austria, when known. */
  movedYear?: number;
  /** Languages they can help in, e.g. ["Russian", "Kazakh", "English"], when known. */
  languages?: string[];
  /** Self-hosted portrait, e.g. "/buddies/kz-1.jpg". Initials if absent. */
  photoUrl?: string;
}

/*
 * Consent, 11 October 2026: Temirlan accepted the Buddy Agreement with photo
 * consent in the app on 9 October; keep his written reply as the record. If he
 * says no or withdraws: remove the entry and the photo within 7 days.
 * Oleksandr (Ukraine) was shown from 11 to 13 October and is off the site
 * until he finishes joining in the app and asks to be shown again (Noah,
 * 13 Oct). His entry and photo are in git history (main 04ee94d).
 */
export const buddies: Buddy[] = [
  {
    id: "temirlan",
    countryCode: "KZ",
    countryName: "Kazakhstan",
    name: "Temirlan",
    university: "University of Salzburg",
    programme: "Communication and Media Studies",
    city: "Salzburg",
    photoUrl: "/buddies/temirlan.jpg",
  },
];

/** The buddy who covers a country, if there is one. */
export function buddyFor(countryCode: string | null | undefined): Buddy | undefined {
  return countryCode ? buddies.find((b) => b.countryCode === countryCode) : undefined;
}

/** "Aruzhan B." → "AB", for the monogram when there is no photo. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
